import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import Button from 'ui/components/Button/Button';
import EditIcon from '@mui/icons-material/Edit';
import useEditCoach from 'hooks/usecase/useEditCoach';
import EditUser from '../../../pages/user/EditUser';
import ActiveSettingsCoachModal from '../../../pages/coach/ActiveSettingsCoachModal';
import GoBackButton from 'ui/components/shared/GoBackButton';
import ViewModal from '../ViewModal';
import { useMeQuery } from 'hooks/queries';
import CoachView from './CoachView';
import CoachActivationSwitch from './CoachActivationSwitch';
import CoachExceedMaxCapacitySwitch from './CoachExceedMaxCapacitySwitch';

import { t } from 'i18next';

const CoachViewModal = ({
  coach = {},
  open,
  company = {},
  onClose,
  isLoading,
  onClickPreviousCoach,
  onClickNextCoach,
  userRole,
  availabilitiesDisabled = false,
  directionalArrowsActivated = false,
  editable = false,
  typeView = 'coach',
  refetch,
  showMaxCapacity = true,
  onRefuseCoachSelfRegistration,
  onValidateCoachSelfRegistration,
  editMode = false,
}) => {
  const [contentType, setContentType] = useState('coach');
  const styleBox = editable ? { paddingBottom: 0 } : {};

  useEffect(() => {
    if (editMode) {
      setContentType('edit');
    }
  }, [editMode]);

  if (isLoading) return <></>;

  const validCoachButton = coach?.rhValidation;

  return (
    <ViewModal
      open={open}
      onClose={onClose}
      customStyleParentContent={{ height: '100%' }}
      customStyleBox={{
        width: '90%',
        borderRadius: '16px',
        position: 'relative',
        height: '70%',
        ...styleBox,
      }}>
      {contentType === 'coach' && (
        <CoachDetail
          coach={coach}
          isLoading={isLoading}
          company={company}
          editable={editable}
          directionalArrowsActivated={directionalArrowsActivated}
          availabilitiesDisabled={availabilitiesDisabled}
          typeView={typeView}
          userRole={userRole}
          refetch={refetch}
          showMaxCapacity={showMaxCapacity}
          validCoachButton={validCoachButton}
          onClickPreviousCoach={onClickPreviousCoach}
          onClickNextCoach={onClickNextCoach}
          onClickEditProfile={() => setContentType('edit')}
          onRefuseCoachSelfRegistration={onRefuseCoachSelfRegistration}
          onValidateCoachSelfRegistration={onValidateCoachSelfRegistration}
        />
      )}

      {contentType === 'edit' && (
        <CoachEditContent
          coach={coach}
          company={company}
          onClickShowProfile={() => setContentType('coach')}
          refetch={refetch}
        />
      )}
    </ViewModal>
  );
};

const CoachDetail = ({
  coach,
  isLoading,
  company,
  directionalArrowsActivated,
  availabilitiesDisabled,
  onClickPreviousCoach,
  onClickNextCoach,
  onClickEditProfile,
  editable,
  userRole,
  typeView,
  refetch,
  showMaxCapacity,
  validCoachButton = false,
  onRefuseCoachSelfRegistration,
  onValidateCoachSelfRegistration,
}) => {
  const { actions, state } = useEditCoach(coach.id, company.id, !editable, refetch);

  return (
    <Box
      sx={{
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>
      <CoachView
        userRole={userRole}
        availabilitiesEditable
        coach={coach}
        isLoading={isLoading}
        company={company}
        typeView={typeView}
        directionalArrowsActivated={directionalArrowsActivated}
        availabilitiesDisabled={availabilitiesDisabled}
        showMaxCapacity={showMaxCapacity}
        onClickPreviousCoach={onClickPreviousCoach}
        onClickNextCoach={onClickNextCoach}
      />

      {editable && !state?.query?.isLoading && (
        <CoachViewModalFooter
          validCoachButton={validCoachButton}
          actions={actions}
          state={state}
          company={company}
          onClickEditProfile={onClickEditProfile}
          showProfileEditButton={true}
          onRefuseCoachSelfRegistration={onRefuseCoachSelfRegistration}
          onValidateCoachSelfRegistration={onValidateCoachSelfRegistration}
        />
      )}
    </Box>
  );
};

const CoachEditContent = ({ coach, company, onClickShowProfile, refetch }) => {
  const { data: user } = useMeQuery();
  const { actions, state } = useEditCoach(coach.id, company.id, false, refetch);

  if (state.query.isLoading) return <></>;

  return (
    <>
      <Box>
        <GoBackButton onClick={onClickShowProfile} />
        <EditUser
          actions={actions}
          state={state}
          userRole={user?.role}
          company={user?.company}
          control={state.form.control}
          typeUser="coach"
          userId={coach.id}
          showBackToProfile={false}
          showSaveButton={false}
          showActiveProfile={false}
        />
      </Box>

      <CoachViewModalFooter actions={actions} state={state} showProfileSaveButton={true} company={company} />
    </>
  );
};

const CoachViewModalFooter = ({
  actions,
  state,
  company,
  onClickEditProfile,
  validCoachButton = false,
  showProfileEditButton = false,
  showProfileSaveButton = false,
  onRefuseCoachSelfRegistration,
  onValidateCoachSelfRegistration,
}) => {
  const [openActiveSettings, setOpenActiveSettings] = useState(false);

  const onChangeActive = () => {
    setOpenActiveSettings(true);
  };

  return (
    <Box
      sx={{
        padding: '16px 32px',
        background: '#FFFFFFE6',
        position: 'sticky',
        bottom: '0',
        marginTop: '16px',
        borderBottomLeftRadius: '16px',
        borderBottomRightRadius: '16px',
        marginLeft: '-32px',
        marginRight: '-32px',
        justifyContent: 'space-between',
        display: 'flex',
        zIndex: '10000',
      }}>
      <Box sx={{ display: 'flex', gap: '24px' }}>
        <CoachActivationSwitch
          active={state.query.data.active}
          deactivatedFrom={state.query.data.deactivatedFrom}
          deactivatedUntil={state.query.data.deactivatedUntil}
          onChange={onChangeActive}
        />

        <CoachExceedMaxCapacitySwitch
          control={state.form.control}
          clientLabel={company.clientLabel}
          onChange={() => actions.submitForm()}
        />
      </Box>

      {showProfileEditButton && !validCoachButton && (
        <Button variant="primary" onClick={onClickEditProfile}>
          <EditIcon sx={{ width: '20px', height: '20px', marginRight: '6px' }} />
          {t('coach_view_edit_profile')}
        </Button>
      )}

      {showProfileSaveButton && (
        <Button
          onClick={() => {
            actions.submitForm();
          }}>
          {t('save_profile')}
        </Button>
      )}

      <ActiveSettingsCoachModal
        open={openActiveSettings}
        control={state.form.control}
        coach={state.query.data}
        setValue={actions.setValue}
        watch={actions.watch}
        onClose={() => {
          actions.resetActive();
          setOpenActiveSettings(false);
        }}
        onValidate={() => {
          setOpenActiveSettings(false);
          actions.submitForm();
        }}
      />

      {validCoachButton && (
        <Box>
          <Button sx={{ marginRight: '16px' }} onClick={onRefuseCoachSelfRegistration} variant="delete">
            {t('decline')}
          </Button>
          <Button onClick={onValidateCoachSelfRegistration}>{t('validate')}</Button>
        </Box>
      )}
    </Box>
  );
};

export default CoachViewModal;
